import {use, useEffect, useState} from 'react'
import {Checkbox, FormControl, InlineLink, Stack, Text, UnorderedList} from '@primer/react-brand/esm'
import {DotFillIcon} from '@primer/octicons-react'
import * as z from 'zod'

import {FormContext} from '../../../FormContext'
import type {GeneralConsentLanguageProps} from './types'
import {getAnalyticsEvent} from '../../../../../../lib/utils/analytics'

import {t} from '../../../../../../lib/utils/localization'

export default function SouthKorea({
  fieldName,
  // eslint-disable-next-line @eslint-react/no-unstable-default-props
  exampleFields = [],
  privacyStatementHref,
  children,
  onValidationChange,
}: GeneralConsentLanguageProps) {
  const fields = exampleFields.map(field => t(field)).join(', ')
  const formContext = use(FormContext)
  const {
    ref,
    onChange: primaryConsentOnChange,
    id,
    ...primaryConsentProps
  } = formContext?.register('primaryConsent', {
    label: 'Required Consent',
    required: true,
    validations: [{message: 'You need to accept the required checkboxes to continue.', schema: z.literal('true')}],
  }) ?? {}
  const primaryConsentError = formContext?.errors['primaryConsent']

  /**
   * Since South Korea requires explicit acceptance of the primary consent, we use false as the default value.
   */
  const [primaryConsentAccepted, setPrimaryConsentAcceptance] = useState(false)

  const onPrimaryConsentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    primaryConsentOnChange?.(event)
    setPrimaryConsentAcceptance(!primaryConsentAccepted)
  }

  /**
   * We call `onValidationChange` with `false` as the component mounts, and
   * then again whenever the primary consent changes.
   */
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(primaryConsentAccepted)
    }
  }, [onValidationChange, primaryConsentAccepted])

  return (
    <Stack direction="vertical" padding="none" gap="condensed">
      <FormControl id={id} required validationStatus={typeof primaryConsentError === 'string' ? 'error' : undefined}>
        <FormControl.Label>
          <Text>{t('I agree to the collection and use of my personal information (required)*.')}</Text>
          <UnorderedList>
            <UnorderedList.Item leadingVisual={DotFillIcon} leadingVisualFill="default">
              <Text>
                {t(
                  'Items of Personal Information to be Collected: {fields}, and any other fields visible on this form.',
                ).replace('{fields}', fields)}
              </Text>
            </UnorderedList.Item>
            <UnorderedList.Item leadingVisual={DotFillIcon} leadingVisualFill="default">
              <Text>
                {t('Purpose of Collection and Use: GitHub will use the data for the purpose described on this form.')}
              </Text>
            </UnorderedList.Item>
            <UnorderedList.Item leadingVisual={DotFillIcon} leadingVisualFill="default">
              <Text>
                {t(
                  'Retention/Use Period of Personal Information: As long as needed to provide the service(s) you are requesting',
                )}
              </Text>
            </UnorderedList.Item>
          </UnorderedList>
        </FormControl.Label>
        <Checkbox
          value="true" // Explicitly setting to true to match the validation schema
          type="checkbox"
          checked={primaryConsentAccepted}
          onChange={onPrimaryConsentChange}
          required
          ref={ref}
          aria-describedby={id && `${id}-validation`}
          {...getAnalyticsEvent({
            action: 'primary_consent_south_korea',
            tag: 'checkbox',
            context: 'form_submit',
            location: 'form',
          })}
          {...primaryConsentProps}
        />
        {typeof primaryConsentError === 'string' ? (
          <FormControl.Validation>{primaryConsentError}</FormControl.Validation>
        ) : null}
      </FormControl>
      <FormControl>
        <FormControl.Label htmlFor={fieldName} data-testid="label">
          <Text>
            {t(
              'I agree to receiving marketing information and use of my personal information for marketing purposes (optional).',
            )}
          </Text>
          <UnorderedList>
            <UnorderedList.Item leadingVisual={DotFillIcon} leadingVisualFill="default">
              <Text>
                <Text as="span" size="350" weight="bold">
                  {t(
                    'Consent to Receive Marketing: The information collected may be used for GitHub for personalized communications, targeted advertising and campaign effectiveness.',
                  )}
                </Text>
              </Text>
            </UnorderedList.Item>
            <UnorderedList.Item leadingVisual={DotFillIcon} leadingVisualFill="default">
              <Text>
                {t(
                  'Items of Personal Information to be Collected: {fields}, and any other fields visible on this form.',
                ).replace('{fields}', fields)}
              </Text>
            </UnorderedList.Item>
            <UnorderedList.Item leadingVisual={DotFillIcon} leadingVisualFill="default">
              <Text>{t('Purpose of Collection and Use: To contact you for marketing purposes.')}</Text>
            </UnorderedList.Item>
            <UnorderedList.Item leadingVisual={DotFillIcon} leadingVisualFill="default">
              <Text>
                {t(
                  'Retention/Use Period of Personal Information: As long as needed to provide the service(s) you are requesting.',
                )}
              </Text>
            </UnorderedList.Item>
          </UnorderedList>
        </FormControl.Label>
        {children}
      </FormControl>
      <Text variant="muted">
        {t(
          'You have the right to refuse the collection and use of your personal information, use of personal information or marketing purposes, and receiving marketing information as set forth above. However, if you refuse, you may not be able to receive the benefits described under Purpose of Collection & Use.',
        )}{' '}
        <InlineLink
          {...getAnalyticsEvent({
            action: t('GitHub Privacy Statement'),
            tag: 'hyperlink',
            context: 'consent_language_south_korea',
            location: 'form',
          })}
          href={privacyStatementHref}
        >
          {t('GitHub Privacy Statement')}
        </InlineLink>
        .
      </Text>
    </Stack>
  )
}
